import { StatusCard } from "./../../status-card";
import { P2Button } from "react-bootstrap-common-script";
import { StatusUpdateInterface } from "../../../models/interfaces/update-status.interface";
import { useNavigate } from "react-router-dom";
import { Helpers } from "../../../utils/helper";
import { useCallback, useMemo } from "react";
import useIsMobile from "../../../hooks/use-is-mobile";
import { PolymerAxios } from "../../../utils/axios";
import { API_ApplicationDetail } from "../../../utils/api";
import { useAppSelector, useAppDispatch } from "../../../redux/hook";
import {
  setApplicationDetails,
  setCancelledBy,
} from "../../../redux/slices/application-cancelled-slice";
import { BB13ApplicationStatusInterface } from "../../../models/interfaces/bb13ApplicationStatus.interface";
import { openRouteStageD } from "../../../redux/slices/routes-management-slice";
import expiryDuration from "../../../config/expiry_duration";
import { ApplicationStatusEnum } from "../../../models/enum/application-status.enum";
import { useGetStageD1ObjQuery } from "../../../redux/services/getStageD";
import moment from "moment";
import { setApplicationId } from "../../../redux/slices/application-id-slice";
import useStageDStepper from "../../../hooks/use-stage-d-stepper";
import { CANCELLED_STATUSES } from "../../../utils/shared/constants";
import config from '../../../config';
interface TrackerItemInterface {
  item?: StatusUpdateInterface;
  debug?: boolean;
  itemBB13?: BB13ApplicationStatusInterface;
}
const getVariant = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  if (itemIndex === 5) {
    // Specific case when itemIndex = 5 and appStatus = PREPARE_FOR_COMPLETION
    if (item.application_status === "PREPARE_FOR_COMPLETION") {
      if (
        Helpers.isTodayAfterGivenDate(item.completion_date) &&
        item.polymer_status === "E2"
      ) {
        return "light_complete";
      }
    }

    if (tempIndex < 5) {
      return "pending";
    } else if (
      item.application_status === "APPLICATION_APPROVED" ||
      item.application_status === "APPLICATION_COMPLETED" ||
      item.application_status === "CLOSED" ||
      item.application_status === "PREPARE_FOR_COMPLETION_COMPLETE"
    ) {
      return "light_complete";
    } else if (item.application_status === "APPLICATION_REJECTED") {
      return "reject";
    } else {
      return "cancel";
    }
  } else if (itemIndex !== 0) {
    const cancelledRejectedStatus: ApplicationStatusEnum[] = [
      "CANCELLED",
      "REJECTED",
    ];
    if (cancelledRejectedStatus.includes(item.application_status)) {
      let currentIndex: number;
      switch (item?.code) {
        case "UNCNoSubmission":
          currentIndex = 0;
          break;
        case "UNCIncompleteEndorse":
          currentIndex = 1;
          break;
        case "UNCNoPayment":
          currentIndex = 2;
          break;
        case "UNCNoDocument":
          currentIndex = 3;
          break;
        default:
          currentIndex = 0;
          break;
      }

      return itemIndex <= currentIndex ? "default" : "dot";
    }
    return tempIndex === itemIndex ? "default" : "dot";
  } else {
    return "default";
  }
};

const getTitleIndex0 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  return tempIndex === 0 ? "Application is pending" : "Application created";
};

const getTitleIndex1 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  return tempIndex < 1 || item.gather_consent_complete_date === ""
    ? "Gather consent from all other owners"
    : "Consent gathered";
};

const getTitleIndex2 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  return tempIndex <= 2 || item.payment_date === ""
    ? "Make payment"
    : "Payment completed";
};

const getTitleIndex3 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  if (
    tempIndex < 3 ||
    (tempIndex === 5 && item.application_processed_date === "")
  ) {
    return "Process application";
  } else if (tempIndex === 3) {
    return "Processing application";
  } else {
    return "Application processed";
  }
};

const getTitleIndex4 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  if (item.application_status === "PREPARE_FOR_COMPLETION_COMPLETE") {
    return "Completion attended";
  }

  const today = moment().startOf("day");
  const completionDate = moment(item.completion_date).startOf("day");
  if (tempIndex === 4 && completionDate.isSame(today)) {
    return "Attending completion appointment";
  }
  return "Prepare for completion appointment";
};

const getTitleIndex5 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {

  // Specific case when appStatus = PREPARE_FOR_COMPLETION
  // Check if Completion appointment attended (meaning polymerstage will be in E2)
  if (item?.application_status === "PREPARE_FOR_COMPLETION") {
    if (
      Helpers.isTodayAfterGivenDate(item.completion_date) &&
      item.polymer_status === "E2"
    ) {
      return "Completion appointment attended";
    }
  }

  // Check based on tempIndex
  if (tempIndex < 5) {
    return "Application is pending";
  }

  // Completed status checks
  if (
    item?.application_status === "APPLICATION_APPROVED" ||
    item?.application_status === "APPLICATION_COMPLETED" ||
    item?.application_status === "CLOSED" ||
    item?.application_status === "PREPARE_FOR_COMPLETION_COMPLETE"
  ) {
    return "Application is successfully completed";
  }

  // Rejected status checks
  if (
    item?.application_status === "APPLICATION_REJECTED" ||
    item?.application_status === "REJECTED"
  ) {
    return "Application is rejected";
  }

  // Default case
  return "Application is cancelled";
};

const getTitle = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number
) => {
  switch (itemIndex) {
    case 0:
      return getTitleIndex0(tempIndex, item, itemIndex);
    case 1:
      return getTitleIndex1(tempIndex, item, itemIndex);
    case 2:
      return getTitleIndex2(tempIndex, item, itemIndex);
    case 3:
      return getTitleIndex3(tempIndex, item, itemIndex);
    case 4:
      return getTitleIndex4(tempIndex, item, itemIndex);
    case 5:
      return getTitleIndex5(tempIndex, item, itemIndex);
    default:
      return "";
  }
};

const getDescriptionIndex1 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  isRejectedOrCancelled: boolean
) => {
  if (tempIndex >= 1) {
    if (item.gather_consent_complete_date === "") {
      if (isRejectedOrCancelled) {
        if (item.code === "UNCIncompleteEndorse") {
          return <p className="polymer-tracker-item__warning-text">Overdue</p>;
        }
        return "";
      } else {
        const softOverDueDay = expiryDuration.trackerStageB;
        const daysAgo = Helpers.formatGetDayAgo(
          item.submitted_date
            ? item.submitted_date
            : item.application_create_date
        );
        const daysLeft = softOverDueDay - daysAgo;
        return `${daysLeft >= 0 ? daysLeft : 0} days left`;
      }
    } else {
      return Helpers.formatDate(item.gather_consent_complete_date);
    }
  } else {
    return "";
  }
};

const getDescriptionIndex3 = (
  tempIndex: number,
  item: StatusUpdateInterface,
  isRejectedOrCancelled: boolean,
  actions?: Function,
  isOverdue?: boolean
) => {
  const visibleApplicationStatus = [
    "PENDING_APPLICANT_HDB_REVIEW",
    "APPROVE",
    "REVIEW_FINANCIAL_PLAN",
  ];
  const isViewDocumentVisible = visibleApplicationStatus.includes(
    item.application_status
  );
  if (tempIndex >= 3) {
    if (item.application_processed_date === "") {
      if (isRejectedOrCancelled || !isViewDocumentVisible) {
        if (item.code === "UNCNoDocument" || isOverdue) {
          return <p className="polymer-tracker-item__warning-text">Overdue</p>;
        }
        return "";
      } else {
        const softOverDueDay = expiryDuration.trackerStageD;
        const daysAgo = Helpers.formatGetDayAgo(
          item.submitted_date
            ? item.submitted_date
            : item.application_create_date
        );

        if (isOverdue) {
          return <p className="polymer-tracker-item__warning-text">Overdue</p>;
        } else {
          const daysLeft = softOverDueDay - daysAgo;
          return (
            <div>
              <a
                href="#"
                onClick={() => {
                  if (actions) {
                    actions();
                  }
                }}
                className="polymer-text-links"
              >{`View Document >`}</a>
            </div>
          );
        }
      }
    } else {
      return Helpers.formatDate(item.application_processed_date);
    }
  } else {
    return "";
  }
};

const getDescription = (
  tempIndex: number,
  item: StatusUpdateInterface,
  itemIndex: number,
  isRejectedOrCancelled: boolean,
  actions?: Function,
  isOverdue?: boolean
) => {
  switch (itemIndex) {
    case 0:
      return Helpers.formatDate(
        item.submitted_date ? item.submitted_date : item.application_create_date
      );
    case 1:
      return getDescriptionIndex1(tempIndex, item, isRejectedOrCancelled);
    case 2:
      return item.payment_date === ""
        ? ""
        : Helpers.formatDate(item.payment_date);
    case 3:
      return getDescriptionIndex3(
        tempIndex,
        item,
        isRejectedOrCancelled,
        actions,
        isOverdue
      );
    case 4:
      return item.completion_date === ""
        ? ""
        : Helpers.formatDate(item.completion_date);
    case 5:
      return item.application_completed_date === ""
        ? ""
        : Helpers.formatDate(item.application_completed_date);
    default:
      return "";
  }
};

export const getStatusCardItems = (
  item: StatusUpdateInterface,
  viewDocumentAction?: Function,
  isOverdue?: boolean
) => {
  let tempIndex = 0;
  switch (item.application_status) {
    case "SUBMIT_COMPLETE":
      tempIndex = 1;
      break; //gathering consent
    case "GATHER_CONSENT_COMPLETE":
    case "PENDING_PAYMENT":
    case "PAYMENT_COMPLETE": //make payment
      tempIndex = 2;
      break;
    case "SENT_TO_BIOME": //Processing application
    case "HDB_REVIEW": //Processing application
    case "PENDING_APPLICANT_HDB_REVIEW": //Processing application
    case "APPROVE": //Processing application
    case "PENDING_BOOKING_APPOINTMENT": //Processing application
    case "REVIEW_FINANCIAL_PLAN": //Processing application
      tempIndex = 3;
      break;
    case "PREPARE_FOR_COMPLETION": //prepare for completion
      tempIndex = 4;
      break;
    case "APPLICATION_APPROVED":
    case "APPLICATION_COMPLETED":
    case "APPLICATION_REJECTED":
    case "APPLICATION_CANCELLED":
    case "REJECTED":
    case "CANCELLED":
    case "CANCELLED_WITH_PENDING_PAYMENT":
    case "ARCHIVED":
    case "CLOSED":
    case "PREPARE_FOR_COMPLETION_COMPLETE": //prepare for completion
      tempIndex = 5;
      break;
  }

  const isRejectedOrCancelled =
    CANCELLED_STATUSES.includes(item.application_status) ||
    item.application_status === "ARCHIVED";

  let statusCardItems = [
    {
      isActive: true,
      variant: getVariant(tempIndex, item, 0),
      title: getTitle(tempIndex, item, 0),
      description: getDescription(tempIndex, item, 0, isRejectedOrCancelled),
    },
    {
      isActive: isRejectedOrCancelled
        ? item.gather_consent_complete_date !== ""
        : tempIndex !== 0,
      variant: getVariant(tempIndex, item, 1),
      title: getTitle(tempIndex, item, 1),
      description: getDescription(tempIndex, item, 1, isRejectedOrCancelled),
    },
    {
      isActive: isRejectedOrCancelled
        ? item.payment_date !== ""
        : tempIndex >= 2,
      variant: getVariant(tempIndex, item, 2),
      title: getTitle(tempIndex, item, 2),
      description: getDescription(tempIndex, item, 2, isRejectedOrCancelled),
    },
    {
      isActive: isRejectedOrCancelled
        ? item.application_processed_date !== ""
        : tempIndex >= 3,
      variant: getVariant(tempIndex, item, 3),
      title: getTitle(tempIndex, item, 3),
      description: getDescription(
        tempIndex,
        item,
        3,
        isRejectedOrCancelled,
        viewDocumentAction,
        isOverdue
      ),
    },
    {
      isActive: isRejectedOrCancelled
        ? item.completion_date !== ""
        : tempIndex >= 4,
      variant: getVariant(tempIndex, item, 4),
      title: getTitle(tempIndex, item, 4),
      description: getDescription(tempIndex, item, 4, isRejectedOrCancelled),
    },
    {
      isActive: tempIndex === 5,
      variant: getVariant(tempIndex, item, 5),
      title: getTitle(tempIndex, item, 5),
      description: getDescription(tempIndex, item, 5, isRejectedOrCancelled),
    },
  ];

  return statusCardItems;
};

function TrackerItem({ item, itemBB13, ...props }: TrackerItemInterface) {
  const { isMobile } = useIsMobile();
  const loginUser = useAppSelector(
    (state) => state.auth.AuthenticationSlice.loginUser
  );
  const singpassToken = useAppSelector(
    (state) => state.auth.AuthenticationSlice.singpassToken
  );
  const stageDApplicationStatuses = [
    "SENT_TO_BIOME",
    "HDB_REVIEW",
    "PENDING_APPLICANT_HDB_REVIEW",
  ];
  const { data: StageDData } = useGetStageD1ObjQuery(
    {
      application_uuid: item?.application_id ?? "",
      uinfin: loginUser?.uinfin ?? "",
      stage: "D1",
      singpass_token: singpassToken ?? "",
    },
    {
      skip:
        !item?.application_id ||
        !stageDApplicationStatuses.includes(item.application_status),
    }
  );
  let navigate = useNavigate();
  const AppDispatch = useAppDispatch();
  const { stageD3RedirectURL } = useStageDStepper(
    loginUser.uinfin,
    item?.application_id ?? ""
  );

  const isOverdue = useMemo(() => {
    let isStageDOverdue: boolean = false;
    if (StageDData) {
      const currentDate = moment();
      let requestsKeyDates: any = [];
      if (StageDData?.message_info) {
        requestsKeyDates = requestsKeyDates.concat(
          StageDData?.message_info.map((item) => {
            return {
              received_date: item.received_date,
              due_date: item.due_date,
              type: "message",
            };
          })
        );
      }

      if (StageDData?.solicitor_message_info) {
        requestsKeyDates = requestsKeyDates.concat(
          StageDData?.solicitor_message_info.map((item) => {
            return {
              received_date: item.received_date,
              due_date: item.due_date,
              type: "solicitor_message",
            };
          })
        );
      }

      if (StageDData?.folders) {
        requestsKeyDates = requestsKeyDates.concat(
          StageDData?.folders.map((item) => {
            return {
              received_date: item.received_date,
              due_date: item.due_date,
              type: "document",
            };
          })
        );
      }

      if (StageDData?.endorse_forms) {
        requestsKeyDates = requestsKeyDates.concat(
          StageDData?.endorse_forms.map((item) => {
            return {
              received_date: item.received_date,
              due_date: item.due_date,
              type: "endorse_form",
            };
          })
        );
      }

      let daysDifference: number = -1;
      for (const keyDate of requestsKeyDates) {
        const lapsedDuration = moment(currentDate).diff(
          moment(keyDate.received_date),
          "days"
        );
        if (lapsedDuration > daysDifference) {
          daysDifference = lapsedDuration;
        }
      }
      if (daysDifference > expiryDuration.hdb_review) {
        isStageDOverdue = true;
      }
    }

    return isStageDOverdue;
  }, [StageDData]);

  const getStageDPageUrl = useCallback(() => {
    //Need to add application uuid, because when redirect to in-principle-approval, there will not have application uuid in slice if not addded this.
    AppDispatch(setApplicationId(item?.application_id));

    if (item?.application_status === "SENT_TO_BIOME") {
      //D1
      return `/application-coo/stage-d/hdb-review?application_id=${item?.application_id}`;
    } else if (item?.polymer_status) {
      //NOTE: We checking using polymerStatus is because D2 and D3 next button will update the polymerStatus, So that they will go through all the D2 and D3 regardless of application status.
      // The scenario when prepare_for_completion status is assigned but applicant haven't completely go through D2 and D3 yet.

      if (
        item?.polymer_status === "D3" &&
        (item?.application_status === "REVIEW_FINANCIAL_PLAN" ||
          item?.application_status === "PREPARE_FOR_COMPLETION")
      ) {
        return stageD3RedirectURL;
      } else {
        return `/application-coo/stage-d/in-principle-approval?application_id=${item?.application_id}`;
      }
    } else if (item?.application_status === "APPROVE") {
      //In case polymerStatus is empty when in STAGE D, then use back item?.application_status
      return `/application-coo/stage-d/in-principle-approval?application_id=${item?.application_id}`;
    } else if (item?.application_status === "REVIEW_FINANCIAL_PLAN") {
      return stageD3RedirectURL;
    } else {
      return `?application_id=${item?.application_id}`;
    }
  }, [item?.application_id, item?.application_status, item?.polymer_status]);

  const redirectToViewDocument = () => {
    AppDispatch(openRouteStageD(true));
    //DO MAPPING TO APPLICATION SLICES
    setTimeout(() => {
      window.location.href = `/transfer${getStageDPageUrl()}`;
    }, 500);
  };

  const handleNavigation = useCallback(
    (applicationId: string) => async () => {
      if (!item) {
        return;
      }

      console.log("item - ", item )
      console.log("itemBB13 - ", itemBB13 )

      // Redirect for BB13, byPass using env config flag
      if (itemBB13 && itemBB13.link && (item.application_status === "APPROVE" || item.application_status === "REVIEW_FINANCIAL_PLAN" || item.application_status === "PREPARE_FOR_COMPLETION") && !config.byPassBB13) {
        //when have itemBB13 with not itemBB13.status != "X"
        window.open(itemBB13.link, "_blank");
        return;
      }

      if (!CANCELLED_STATUSES.includes(item.application_status)) {
        navigate(`/application-coo?application_id=${item.application_id}`);
      }

      let cancellationReason = "";
      const { data } = await PolymerAxios(
        loginUser?.uinfin ?? "",
        singpassToken
      ).get(`${API_ApplicationDetail}?applicationUuid=${applicationId}`);

      switch (item.code) {
        case "UNCNoSubmission":
          cancellationReason = "overdue for application submission";
          break;
        case "UNCIncompleteEndorse":
          cancellationReason = "overdue for consents and endorsements";
          break;
        case "UNCNoPayment":
          cancellationReason = "overdue for payment";
          break;
        case "UNCNoDocument":
          cancellationReason = "overdue for supporting documents upload";
          break;
        default:
          cancellationReason =
            data?.cancellation_reason ?? item.cancellation_reason;
      }

      switch (item.application_status) {
        case "APPLICATION_CANCELLED": {
          AppDispatch(
            setApplicationDetails({
              ...data,
              cancellation_reason: cancellationReason,
              code: item?.code,
            })
          );
          AppDispatch(setCancelledBy("owner"));
          navigate(`/application-coo/application-cancelled`);
          break;
        }
        case "CANCELLED": {
          AppDispatch(
            setApplicationDetails({
              ...data,
              cancellation_reason: cancellationReason,
              code: item?.code,
            })
          );
          AppDispatch(setCancelledBy("officer"));
          navigate(`/application-coo/application-cancelled`);
          break;
        }
        case "CANCELLED_WITH_PENDING_PAYMENT": {
          AppDispatch(
            setApplicationDetails({
              ...data,
              cancellation_reason: cancellationReason,
              code: item?.code,
            })
          );
          AppDispatch(setCancelledBy("officer"));
          navigate(
            `/application-coo/cancel-application-payment?application_id=${item.application_id}`
          );
          break;
        }
        case "REJECTED": {
          AppDispatch(
            setApplicationDetails({
              ...data,
              cancellation_reason: cancellationReason,
              code: item?.code,
            })
          );
          AppDispatch(setCancelledBy("officer"));
          navigate(`/application-coo/application-cancelled`);
          break;
        }
      }
    },
    [AppDispatch, item, loginUser?.uinfin, navigate, singpassToken, itemBB13]
  );

  if (item) {
    const address = item.address;
    return (
      <div className={`polymer-tracker-item polymer-tracker-item__wrapper`}>
        <div className="polymer-tracker-item__content">
          <>
            <p
              className="polymer-tracker-item__warning-text"
              style={{ display: props.debug ? "unset" : "none" }}
            >
              {item.application_id}
            </p>
            <p className="polymer-tracker-item__content-title">
              Change in Flat Ownership
            </p>
            <p className="polymer-tracker-item__content-description">
              {address}
            </p>
            <div style={{ marginTop: 24, flexGrow: 1 }}>
              <StatusCard
                items={getStatusCardItems(
                  item,
                  redirectToViewDocument,
                  isOverdue
                )}
              />
            </div>
            {item.application_status !== "CLOSED" && (
              <div
                style={{ marginTop: !isMobile ? 115 : 32 }}
                className="d-flex justify-content-center polymer-button-wrapper"
              >
                <P2Button
                  onClick={handleNavigation(item.application_id)}
                  variant={"outline-primary"}
                  width="203px"
                >
                  View application status
                </P2Button>
              </div>
            )}
          </>
        </div>
      </div>
    );
  } else if (itemBB13) {
    return (
      <div className={`polymer-tracker-item polymer-tracker-item__wrapper`}>
        <div className="polymer-tracker-item__content">
          <>
            <p className="polymer-tracker-item__content-title">
              Change in Flat Ownership
            </p>
            <p className="polymer-tracker-item__content-description">
              {itemBB13.address}
            </p>
            <div
              style={{ marginTop: 32 }}
              className="d-flex justify-content-center polymer-button-wrapper"
            >
              <P2Button
                onClick={() =>
                  setTimeout(() => window.open(itemBB13.link, "_blank"))
                }
                variant={"outline-primary"}
                width="203px"
              >
                View application status
              </P2Button>
            </div>
          </>
        </div>
      </div>
    );
  } else {
    return (
      <div
        aria-disabled={true}
        className={`polymer-tracker-item polymer-tracker-item__wrapper polymer-tracker-item__wrapper-border_disabled`}
      >
        <div className="polymer-tracker-item__content">
          <>
            <p className="polymer-tracker-item__content-title">
              No status update is available
            </p>
            <p className="polymer-tracker-item__content-description">
              Come back here when you have initiated an e-service.
            </p>
          </>
        </div>
      </div>
    );
  }
}

export default TrackerItem;
